import { QueryClient, QueryKey } from '@tanstack/react-query';
import { OrderBy } from '../../components/Hooks/useList.types';
import {
  InfiniteDataQueryCache,
  UseInfiniteDataQueryFn,
} from '../useInfiniteDataQuery/useInfiniteDataQuery.types';
import { UseInfiniteDataQueryUtils } from '../useInfiniteDataQuery/utils/useInfiniteDataQuery.utils';

export const findPageIndexByItemId = <PageData>(
  cache: InfiniteDataQueryCache<PageData> | undefined,
  itemId: string,
): number | null => {
  const pages = cache?.pages;
  if (!pages?.length) {
    return null;
  }
  const pageIndex = pages.findIndex((page) =>
    page.data.some((item) => (item as { id?: string })?.id === itemId),
  );
  return pageIndex === -1 ? null : pageIndex;
};

interface RefetchSinglePageParams<PageData> {
  queryClient: QueryClient;
  queryKey: QueryKey;
  pageIndex: number;
  limit: number;
  defaultOrderBy: OrderBy;
  queryFn: UseInfiniteDataQueryFn<PageData>;
}

export const refetchSinglePage = async <PageData>({
  queryClient,
  queryKey,
  pageIndex,
  limit,
  defaultOrderBy,
  queryFn,
}: RefetchSinglePageParams<PageData>): Promise<void> => {
  await queryClient.cancelQueries({ queryKey });

  const pageParam = pageIndex + 1;
  const skip = (pageParam - 1) * limit;
  const newPage = await queryFn({ skip, limit, order: defaultOrderBy });

  queryClient.setQueryData<InfiniteDataQueryCache<PageData>>(queryKey, (oldData) => {
    if (!oldData) {
      return oldData;
    }
    return {
      ...oldData,
      pages: oldData.pages.map((page, idx) =>
        idx === pageIndex
          ? UseInfiniteDataQueryUtils.toCachedPage({ page: newPage, currentPage: pageParam, limit })
          : page,
      ),
    };
  });
};
